import { getHomeUrl, getStaffRoutePath } from '@/utils/cp-context';
import { STAFF_ROUTES } from '@/constants/staff-routes';
import type { Breadcrumb } from '@ps-refarch-ux/neon';
/**
 * Copy for `EmptyState` when there are no specialization areas (`total === 0`).
 * Matches design: empty card title, body, and CTA (Neon adds the + via `dataIcon="add"` on the button).
 */
export enum EMPTY_SPECIALIZATION_AREAS_TEXT {
  Title = 'No areas',
  Body = 'Create areas to organize courses and support students in exploring areas of interest. ',
  Button = 'Create area',
  NoFilterResultsTitle = 'No results found',
}

export enum EMPTY_SPECIALIZATION_AREAS_ID {
  Button = 'no-specialization-areas',
  ClearFiltersButton = 'specialization-areas-clear-filters',
}

export enum SPECIALIZATION_AREAS_ID {
  Breadcrumbs = 'clusters-breadcrumbs',
  Button = 'create-specialization-area',
  SearchField = 'specialization-areas-search',
  FiltersButton = 'specialization-areas-filters',
  FilterApplyButton = 'specialization-areas-filter-apply',
  FilterCancelButton = 'specialization-areas-filter-cancel',
  PublishedSchoolsInfoTooltip = 'specialization-areas-published-schools-info',
  ListLoadRetry = 'specialization-areas-list-retry',
}

export enum CREATE_AREA_ID {
  Breadcrumbs = 'create-area-breadcrumbs',
  NameField = 'create-area-name',
  DescriptionField = 'create-area-description',
  Footer = 'create-area-footer',
  CancelButton = 'create-area-cancel',
  SaveButton = 'create-area-save',
}

export enum SPECIALIZATION_AREAS_TEXT {
  BreadcrumbHome = 'Home',
  Title = 'Specialization areas',
  Button = 'Create area',
  Filters = 'Filters',
  SearchPlaceholder = 'Search by area or specialization',
  /** Filter drawer — hide rows with no pathway/specialization names. */
  FilterHideNoActiveLabel = 'Hide areas with no active specializations',
  /** Filter drawer — instructional line under the school select. */
  FilterPublishedSchoolsHelper = 'Choose a school to display specialization areas published to it.',
  FilterApply = 'Apply',
  /** Filter chip when “hide areas with no specializations” is turned off. */
  FilterChipShowEmptyAreas = 'Specializations | All areas',
  /** Shown when GET /clusters fails (avoids the total === 0 empty list). */
  ListLoadRetry = 'Try again',
  PublishedSchoolsTooltip = 'Number of schools where the specialization area is published.',
  AreaColumn = 'Area',
  SpecializationsColumn = 'Specializations',
  PublishedSchoolsColumn = 'Published schools',
}

export enum CREATE_AREA_TEXT {
  Title = 'Create area',
  NameLabel = 'Area name',
  DescriptionLabel = 'Description',
  Cancel = 'Cancel',
  Save = 'Save',
  SaveUnavailableHeading = 'Create area is not available yet',
  ToastCreatedSuffix = ' area created',
  ToastSubheading = 'Next, create specializations for this area.',
  ToastCreateSpecialization = 'Create specialization',
}

export enum CREATE_SPECIALIZATION_ID {
  Breadcrumbs = 'create-specialization-breadcrumbs',
  NameField = 'create-specialization-name',
  CreditsField = 'create-specialization-credits',
  ClassYearFrom = 'create-specialization-class-year-from',
  ClassYearTo = 'create-specialization-class-year-to',
  DescriptionField = 'create-specialization-description',
  Footer = 'create-specialization-footer',
  CancelButton = 'create-specialization-cancel',
  SaveButton = 'create-specialization-save',
  SaveAndCoursesButton = 'create-specialization-save-and-courses',
}

export enum CREATE_SPECIALIZATION_TEXT {
  Title = 'Create specialization',
  NameLabel = 'Specialization name',
  ClassYearsLabel = 'Class years',
  ClassYearsHelper = 'Select which class years can access this specialization.',
  DescriptionLabel = 'Description',
  Cancel = 'Cancel',
  Save = 'Save',
  SaveAndSelectCourses = 'Save and select courses',
  ToastSubheading = 'Next, select courses for this specialization.',
  ToastAreas = 'Areas',
  ToastSelectCourses = 'Select courses',
  /** Intro line above the form (matches design). */
  CardIntro = 'Enter specialization details to guide students, including the name, which class years can access it, and a brief description.',
}

export enum SPECIALIZATION_DETAIL_TEXT {
  Tag = 'Specialization',
  DetailsTab = 'Details',
  SelectCoursesTab = 'Select courses',
}

/** Select courses tab — same rule types as POS requirement detail; intro uses “specialization”. */
export const SPECIALIZATION_SELECT_COURSES_INTRO =
  'Create a course rule to define the courses that can satisfy this specialization. You can create multiple rule types within this specialization to show students the most relevant options.';

export enum SPECIALIZATION_AREAS_ICON {
  Add = 'add',
  Filters = 'settings-tune',
}

export const DEFAULT_CLUSTERS_PAGE_SIZE = 25;
export const DEFAULT_AREA_DETAIL_PAGE_SIZE = 25;
export const SPECIALIZATION_DESCRIPTION_MAX_LENGTH = 40;

/** Card title above the specializations grid on the area (cluster) detail page. */
export const AREA_DETAIL_SPECIALIZATIONS_CARD_HEADING = 'Specializations' as const;
/** Primary CTA on the area detail page — opens publish drawer (drawer confirm stays “Publish”). */
export const AREA_DETAIL_PUBLISH_AND_UPDATE_BUTTON = 'Publish and update' as const;
export const SPECIALIZATION_AREA_PUBLISHED_SCHOOLS_TITLE =
  'Publish and update specialization areas' as const;
export const SPECIALIZATION_AREA_PUBLISH_DESCRIPTION_DISTRICT =
  'When you publish, students at the selected schools can start planning specialization areas. Any in-progress student plans will update automatically with changes. Updates may take up to one day.' as const;
export const SPECIALIZATION_ACTIVE_SCHOOLS_TITLE = 'Active schools' as const;
export const SPECIALIZATION_ACTIVE_SCHOOLS_DESCRIPTION_DISTRICT =
  'Status determines whether a specialization is available to students. Inactive specialization are not displayed to students in Course Planner.' as const;

/** NeonPagination `dataPageSizeOptions` — comma-separated string (same pattern as plans of study). */
export const CLUSTERS_PAGE_SIZE_OPTIONS = '25,50,100,200';
export const SPECIALIZATION_AREAS_SKELETON_COLUMNS = ['18%', '44%', '16%', '12%'] as const;
export const AREA_DETAIL_SPECIALIZATIONS_SKELETON_COLUMNS = [
  '18%',
  '10%',
  '12%',
  '28%',
  '18%',
  '10%',
] as const;

export const SPECIALIZATION_AREAS_GRID_HEADER_CLASS =
  'plans-of-study-header plans-of-study-header-left';
export const SPECIALIZATION_AREAS_GRID_HEADER_CLASS_RIGHT =
  'plans-of-study-header plans-of-study-header-right';

export const getSpecializationAreasBreadcrumbs: Breadcrumb[] = [
  { text: SPECIALIZATION_AREAS_TEXT.BreadcrumbHome, url: getHomeUrl() },
  { text: SPECIALIZATION_AREAS_TEXT.Title, url: '#' },
] as const;

export const getCreateAreaBreadcrumbs: Breadcrumb[] = [
  { text: SPECIALIZATION_AREAS_TEXT.BreadcrumbHome, url: getHomeUrl() },
  {
    text: SPECIALIZATION_AREAS_TEXT.Title,
    url: getStaffRoutePath(`/${STAFF_ROUTES.clusterAndPathway.list}`),
  },
  { text: CREATE_AREA_TEXT.Title, url: '#' },
] as const;

export function getCreateSpecializationBreadcrumbs(areaName: string, areaId: string): Breadcrumb[] {
  return [
    { text: SPECIALIZATION_AREAS_TEXT.BreadcrumbHome, url: getHomeUrl() },
    {
      text: SPECIALIZATION_AREAS_TEXT.Title,
      url: getStaffRoutePath(`/${STAFF_ROUTES.clusterAndPathway.list}`),
    },
    {
      text: areaName,
      url: getStaffRoutePath(`/${STAFF_ROUTES.clusterAndPathway.detail(areaId)}`),
    },
    { text: CREATE_SPECIALIZATION_TEXT.Title, url: '#' },
  ];
}

export function getSpecializationDetailBreadcrumbs(
  areaName: string,
  areaId: string,
  specializationName: string
): Breadcrumb[] {
  return [
    { text: SPECIALIZATION_AREAS_TEXT.BreadcrumbHome, url: getHomeUrl() },
    {
      text: SPECIALIZATION_AREAS_TEXT.Title,
      url: getStaffRoutePath(`/${STAFF_ROUTES.clusterAndPathway.list}`),
    },
    {
      text: areaName,
      url: getStaffRoutePath(`/${STAFF_ROUTES.clusterAndPathway.detail(areaId)}`),
    },
    { text: specializationName, url: '#' },
  ];
}
